import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    AppBar,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    IconButton,
    Paper,
    Stack,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AuthPanel from '../../components/auth/AuthPanel';
import { useAuth } from '../../context/AuthContext';
import { getBlogPostUrl } from './blogPosts';

export const HEADER_HEIGHT = 88;

const mainNavItems = [
    { label: 'Возможности', href: '/#structure' },
    { label: 'Задачи', href: '/#tasks' },
    { label: 'Тарифы', href: '/#pricing' },
    { label: 'FAQ', href: '/#faq' },
];

export function BlogHeader() {
    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.94)',
                color: '#101828',
                borderBottom: '1px solid #EAECF0',
                backdropFilter: 'blur(16px)',
            }}
        >
            <Container maxWidth="xl">
                <Toolbar disableGutters sx={{ minHeight: `${HEADER_HEIGHT}px`, gap: 2 }}>
                    <Stack
                        component={RouterLink}
                        to="/"
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ flexGrow: 1, color: 'inherit', textDecoration: 'none' }}
                    >
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #0F172A 0%, #1D4ED8 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#FFFFFF',
                                fontWeight: 800,
                            }}
                        >
                            FR
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>
                                ФинРадар
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#667085', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                Реальная картина бизнеса
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                        {mainNavItems.map((item) => (
                            <Button
                                key={item.href}
                                component="a"
                                href={item.href}
                                sx={{ color: '#344054', px: 2 }}
                            >
                                {item.label}
                            </Button>
                        ))}
                        <Button component={RouterLink} to="/blog/" sx={{ color: '#344054', px: 2 }}>
                            Блог
                        </Button>
                    </Stack>

                    <Button
                        component={RouterLink}
                        to="/vhod/"
                        variant="contained"
                        sx={{
                            minHeight: 44,
                            borderRadius: 999,
                            px: { xs: 2, md: 3 },
                            textTransform: 'none',
                            fontWeight: 700,
                            backgroundColor: '#155EEF',
                            boxShadow: 'none',
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            Попробовать бесплатно
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                            Попробовать
                        </Box>
                    </Button>
                    <Button
                        component={RouterLink}
                        to="/vhod/"
                        variant="text"
                        sx={{
                            display: { xs: 'none', md: 'inline-flex' },
                            borderRadius: 999,
                            px: 2,
                            py: 1.2,
                            textTransform: 'none',
                            fontWeight: 700,
                            color: '#344054',
                        }}
                    >
                        Вход
                    </Button>
                </Toolbar>
            </Container>
        </AppBar>
    );
}

function ShareButton({ label, href, icon, onClick }) {
    const button = (
        <IconButton
            component={href ? 'a' : 'button'}
            href={href}
            target={href ? '_blank' : undefined}
            rel={href ? 'noopener noreferrer' : undefined}
            onClick={onClick}
            sx={{
                width: 42,
                height: 42,
                border: '1px solid #D0D5DD',
                borderRadius: 1.5,
                color: '#344054',
                backgroundColor: '#FFFFFF',
                '&:hover': {
                    borderColor: '#84ADFF',
                    backgroundColor: '#F5F8FF',
                    color: '#155EEF',
                },
            }}
        >
            {icon}
        </IconButton>
    );

    return <Tooltip title={label}>{button}</Tooltip>;
}

export function BlogSidebar({ post }) {
    const canonicalUrl = getBlogPostUrl(post);
    const encodedUrl = encodeURIComponent(canonicalUrl);
    const encodedTitle = encodeURIComponent(post.title);
    const handleCopy = () => {
        if (typeof navigator !== 'undefined' && navigator.clipboard) {
            navigator.clipboard.writeText(canonicalUrl);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                position: { xs: 'static', lg: 'sticky' },
                top: { lg: HEADER_HEIGHT + 24 },
                p: { xs: 2.5, lg: 3 },
                border: '1px solid #EAECF0',
                borderRadius: 2,
                backgroundColor: '#FFFFFF',
                alignSelf: 'start',
            }}
        >
            <Stack spacing={3}>
                <Box>
                    <Typography sx={{ color: '#667085', fontSize: 13, fontWeight: 900, textTransform: 'uppercase' }}>
                        Теги
                    </Typography>
                    <Stack direction={{ xs: 'row', lg: 'column' }} spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                        {post.tags.map((tag) => (
                            <Chip
                                key={tag}
                                label={tag}
                                sx={{
                                    justifyContent: 'flex-start',
                                    borderRadius: 1.5,
                                    fontWeight: 800,
                                    backgroundColor: '#F2F4F7',
                                    color: '#344054',
                                }}
                            />
                        ))}
                    </Stack>
                </Box>

                <Divider />

                <Box>
                    <Typography sx={{ color: '#667085', fontSize: 13, fontWeight: 900, textTransform: 'uppercase' }}>
                        Опубликовано
                    </Typography>
                    <Typography sx={{ mt: 1, color: '#101828', fontSize: 16, fontWeight: 800 }}>
                        {post.datePublishedLabel}
                    </Typography>
                </Box>

                <Divider />

                <Box>
                    <Typography sx={{ color: '#667085', fontSize: 13, fontWeight: 900, textTransform: 'uppercase' }}>
                        Поделиться
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        <ShareButton
                            label="Поделиться во ВКонтакте"
                            href={`https://vk.com/share.php?url=${encodedUrl}&title=${encodedTitle}`}
                            icon={<Typography sx={{ fontSize: 13, fontWeight: 900, lineHeight: 1 }}>VK</Typography>}
                        />
                        <ShareButton
                            label="Поделиться в Telegram"
                            href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`}
                            icon={<TelegramIcon fontSize="small" />}
                        />
                        <ShareButton
                            label="Поделиться в WhatsApp"
                            href={`https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`}
                            icon={<WhatsAppIcon fontSize="small" />}
                        />
                        <ShareButton
                            label="Скопировать ссылку"
                            onClick={handleCopy}
                            icon={<ContentCopyRoundedIcon fontSize="small" />}
                        />
                    </Stack>
                </Box>

                <Divider />

                <Box>
                    <Typography sx={{ color: '#101828', fontSize: 20, fontWeight: 900, lineHeight: 1.2 }}>
                        Проверьте выписку в ФинРадаре
                    </Typography>
                    <Typography sx={{ mt: 1, color: '#475467', fontSize: 15, lineHeight: 1.55 }}>
                        7 дней бесплатно, без банковской карты и долгого внедрения.
                    </Typography>
                    <Button
                        component={RouterLink}
                        to="/vhod/"
                        variant="contained"
                        endIcon={<ArrowForwardRoundedIcon />}
                        fullWidth
                        sx={{
                            mt: 2,
                            minHeight: 46,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontWeight: 900,
                            backgroundColor: '#155EEF',
                            boxShadow: 'none',
                        }}
                    >
                        Попробовать
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );
}

function ArticleImage({ image }) {
    return (
        <Box component="figure" sx={{ m: 0, my: { xs: 4, md: 5 } }}>
            <Box
                component="img"
                src={image.src}
                alt={image.alt}
                loading="lazy"
                sx={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    border: '1px solid #D0D5DD',
                    borderRadius: 2,
                    backgroundColor: '#FFFFFF',
                }}
            />
            <Typography component="figcaption" sx={{ mt: 1.5, color: '#667085', fontSize: 14, lineHeight: 1.6 }}>
                {image.caption}
            </Typography>
        </Box>
    );
}

function Paragraph({ children }) {
    return (
        <Typography sx={{ color: '#344054', fontSize: { xs: 17, md: 18 }, lineHeight: 1.78, mt: 2 }}>
            {children}
        </Typography>
    );
}

function Lead({ children }) {
    return (
        <Typography sx={{ mt: 2.5, color: '#475467', fontSize: { xs: 19, md: 21 }, lineHeight: 1.65 }}>
            {children}
        </Typography>
    );
}

function SectionTitle({ children, component = 'h2' }) {
    return (
        <Typography
            component={component}
            sx={{
                color: '#101828',
                fontSize: component === 'h3' ? { xs: 24, md: 29 } : { xs: 28, md: 36 },
                fontWeight: 900,
                lineHeight: 1.18,
                mt: component === 'h3' ? 4 : { xs: 6, md: 7 },
                mb: 1,
            }}
        >
            {children}
        </Typography>
    );
}

function BulletList({ items }) {
    return (
        <Box component="ul" sx={{ pl: 3, mt: 2, mb: 0, color: '#344054' }}>
            {items.map((item) => (
                <Typography component="li" key={item} sx={{ pl: 0.5, mb: 1, fontSize: { xs: 17, md: 18 }, lineHeight: 1.7 }}>
                    {item}
                </Typography>
            ))}
        </Box>
    );
}

function CtaBlock({ title, text }) {
    return (
        <Paper
            elevation={0}
            sx={{
                my: { xs: 5, md: 6 },
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                border: '1px solid #B2CCFF',
                backgroundColor: '#EFF4FF',
            }}
        >
            <Stack spacing={2.5} alignItems="flex-start">
                <Box>
                    <Typography component="h2" sx={{ color: '#101828', fontSize: { xs: 25, md: 32 }, fontWeight: 900, lineHeight: 1.18 }}>
                        {title}
                    </Typography>
                    <Typography sx={{ mt: 1, color: '#344054', fontSize: 18, lineHeight: 1.65 }}>
                        {text}
                    </Typography>
                </Box>
                <Button
                    component={RouterLink}
                    to="/vhod/"
                    variant="contained"
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                        minHeight: 48,
                        borderRadius: 1.5,
                        px: 2.5,
                        textTransform: 'none',
                        fontWeight: 900,
                        boxShadow: 'none',
                    }}
                >
                    Попробовать бесплатно
                </Button>
            </Stack>
        </Paper>
    );
}

function SignupBlock() {
    const { establishSession } = useAuth();

    return (
        <Paper
            elevation={0}
            sx={{
                my: { xs: 4, md: 5 },
                p: { xs: 2.5, md: 3.5 },
                border: '1px solid #D0D5DD',
                borderRadius: 2,
                backgroundColor: '#F8FAFC',
            }}
        >
            <AuthPanel
                initialMode="signup"
                showRecoveryTab={false}
                onAuthenticated={() => {
                    establishSession();
                    window.location.href = '/app/';
                }}
                copy={{
                    signupDescription: 'Заполните форму, получите письмо активации и переходите к первой проверке выписок.',
                    signupSubmitLabel: 'Получить письмо активации',
                    signupSubmitPendingLabel: 'Отправляем...',
                    signupSuccessAlert: 'Письмо для активации отправлено, его доставка может занять до 2 минут. Перейдите по ссылке из email и создайте пароль.',
                    loginTitle: 'Вход',
                    loginDescription: 'Войдите в существующий аккаунт.',
                }}
            />
        </Paper>
    );
}

function FaqBlock({ items }) {
    return (
        <Box sx={{ mt: { xs: 6, md: 7 } }}>
            <SectionTitle>Частые вопросы</SectionTitle>
            <Stack spacing={2}>
                {items.map((item) => (
                    <Paper
                        key={item.question}
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            border: '1px solid #EAECF0',
                            borderRadius: 2,
                            backgroundColor: '#FCFCFD',
                        }}
                    >
                        <Typography component="h3" sx={{ color: '#101828', fontSize: { xs: 19, md: 21 }, fontWeight: 900, lineHeight: 1.25 }}>
                            {item.question}
                        </Typography>
                        <Typography sx={{ mt: 1, color: '#344054', fontSize: { xs: 16, md: 17 }, lineHeight: 1.65 }}>
                            {item.answer}
                        </Typography>
                    </Paper>
                ))}
            </Stack>
        </Box>
    );
}

function renderArticleBlock(block, index) {
    const key = `${block.type}-${index}`;

    switch (block.type) {
        case 'lead':
            return <Lead key={key}>{block.text}</Lead>;
        case 'paragraph':
            return <Paragraph key={key}>{block.text}</Paragraph>;
        case 'divider':
            return <Divider key={key} sx={{ my: { xs: 4, md: 5 } }} />;
        case 'h2':
            return <SectionTitle key={key}>{block.text}</SectionTitle>;
        case 'h3':
            return <SectionTitle key={key} component="h3">{block.text}</SectionTitle>;
        case 'list':
            return <BulletList key={key} items={block.items} />;
        case 'image':
            return <ArticleImage key={key} image={block.image} />;
        case 'cta':
            return <CtaBlock key={key} title={block.title} text={block.text} />;
        case 'signup':
            return <SignupBlock key={key} />;
        case 'faq':
            return <FaqBlock key={key} items={block.items} />;
        default:
            return null;
    }
}

export function BlogArticle({ post }) {
    return (
        <Paper
            component="article"
            elevation={0}
            sx={{
                p: { xs: 3, sm: 4, md: 6 },
                border: '1px solid #EAECF0',
                borderRadius: 2,
                backgroundColor: '#FFFFFF',
            }}
        >
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2.5 }}>
                <Chip
                    size="small"
                    icon={<AccessTimeRoundedIcon />}
                    label={post.readingTime}
                    sx={{
                        fontWeight: 800,
                        backgroundColor: '#F2F4F7',
                        color: '#475467',
                        '& .MuiChip-icon': {
                            color: '#667085',
                        },
                    }}
                />
            </Stack>

            <Typography
                component="h1"
                sx={{
                    color: '#101828',
                    fontSize: { xs: 38, md: 58 },
                    fontWeight: 900,
                    lineHeight: 1.04,
                }}
            >
                {post.title}
            </Typography>

            {post.content.map(renderArticleBlock)}
        </Paper>
    );
}
